import type { BankStatementEvidenceBatch } from "@workspace/api-client-react";
import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Upload, FileText, Archive, AlertTriangle } from "lucide-react";

function pence(p: number) {
  return `£${(p / 100).toFixed(2)}`;
}

type Parsed = {
  entries: {
    entryDate: string;
    description: string;
    type: string | null;
    direction: "in" | "out";
    amountPence: number;
  }[];
  periodFrom: string | null;
  periodTo: string | null;
  totalInPence: number;
  totalOutPence: number;
  warnings: string[];
};
type Batch = BankStatementEvidenceBatch;
type Recon = {
  evidenceState?: "review";
  bankArrivalsVerified?: false;
  comparisonBasis?: string;
  bank: {
    totalInPence: number;
    totalOutPence: number;
    stripeInPence: number;
    personPaymentsOutPence: number;
    cardExpensesPence: number;
  };
  cashbook: {
    totalInPence: number;
    totalOutPence: number;
    cardIncomePence: number;
    driverPayoutsPence: number;
  };
  gaps: { stripeVsCardIncomePence: number; driverPayoutsPence: number };
};

export default function AdminBankReconciliation() {
  useAuth();
  const token = localStorage.getItem("ayrshire_token");
  const h = { Authorization: `Bearer ${token}` };
  const { toast } = useToast();

  const [text, setText] = useState("");
  const [filename, setFilename] = useState("statement");
  const [source, setSource] = useState<"csv" | "pdf">("pdf");
  const [preview, setPreview] = useState<Parsed | null>(null);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [recon, setRecon] = useState<{ id: number; data: Recon } | null>(null);
  const [busy, setBusy] = useState(false);

  const loadBatches = useCallback(async () => {
    try {
      const r = await fetch(
        `/api/admin/bank-statements${showArchived ? "?archived=true" : ""}`,
        { headers: h },
      );
      if (!r.ok) throw new Error("Could not load statements");
      const d = (await r.json()) as { items: Batch[] };
      setBatches(d.items ?? []);
    } catch {
      toast({ title: "Could not load statements", variant: "destructive" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);
  useEffect(() => {
    void loadBatches();
  }, [loadBatches]);

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreview(null);
    setText("");
    setFilename(f.name);
    setSource(f.name.toLowerCase().endsWith(".csv") ? "csv" : "pdf");
    if (f.name.toLowerCase().endsWith(".csv")) {
      const reader = new FileReader();
      setBusy(true);
      reader.onload = () => {
        setText(String(reader.result ?? ""));
        setBusy(false);
      };
      reader.onerror = () => {
        setBusy(false);
        toast({ title: "Could not read statement", variant: "destructive" });
      };
      reader.onabort = () => setBusy(false);
      reader.readAsText(f);
    } else {
      toast({
        title: "PDF selected",
        description:
          "Open the PDF, copy the transactions, and paste them below — then Preview.",
      });
    }
  }

  async function doPreview() {
    if (!text.trim()) {
      toast({
        title: "Paste or upload a statement first",
        variant: "destructive",
      });
      return;
    }
    setPreview(null);
    setBusy(true);
    try {
      const r = await fetch("/api/admin/bank-statements/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...h },
        body: JSON.stringify({ text }),
      });
      if (!r.ok) throw new Error("Preview failed");
      setPreview((await r.json()) as Parsed);
    } catch {
      toast({ title: "Preview failed", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  }

  async function commit() {
    setBusy(true);
    try {
      const r = await fetch("/api/admin/bank-statements/commit", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...h },
        body: JSON.stringify({ text, filename, source }),
      });
      if (!r.ok)
        throw new Error(
          ((await r.json()) as { error?: string }).error ?? "Failed",
        );
      toast({ title: "Statement saved" });
      setText("");
      setPreview(null);
      void loadBatches();
    } catch (err) {
      toast({
        title: "Could not save",
        description: (err as Error).message,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  }

  const [stripePayouts, setStripePayouts] = useState<
    {
      amountPence: number;
      status: string;
      arrivalDate: string;
      description: string | null;
    }[]
  >([]);

  async function openRecon(id: number) {
    try {
      const r = await fetch(`/api/admin/bank-statements/${id}/reconcile`, {
        headers: h,
      });
      if (!r.ok) throw new Error("Statement comparison unavailable");
      const d = (await r.json()) as Recon & {
        batch?: { periodFrom?: string; periodTo?: string };
      };
      setRecon({ id, data: d });
      // Pull Stripe's own payouts for the same period — these tie to the bank's
      // "STRIPE PAYMENTS UK" credits to the penny.
      const b = d.batch;
      if (b?.periodFrom && b?.periodTo) {
        try {
          const sr = await fetch(
            `/api/admin/stripe-payouts?from=${b.periodFrom}T00:00:00Z&to=${b.periodTo}T23:59:59Z`,
            { headers: h },
          );
          const sd = (await sr.json()) as { payouts: typeof stripePayouts };
          setStripePayouts(sd.payouts ?? []);
        } catch {
          setStripePayouts([]);
        }
      } else setStripePayouts([]);
    } catch {
      toast({ title: "Could not compare statement", variant: "destructive" });
    }
  }

  async function del(id: number) {
    if (
      !confirm(
        "Archive this statement? Its entries remain preserved and will be excluded from active reconciliation.",
      )
    )
      return;
    try {
      const response = await fetch(`/api/admin/bank-statements/${id}`, {
        method: "DELETE",
        headers: h,
      });
      if (!response.ok) {
        toast({ title: "Could not archive statement", variant: "destructive" });
        return;
      }
      if (recon?.id === id) setRecon(null);
      void loadBatches();
    } catch {
      toast({ title: "Could not archive statement", variant: "destructive" });
    }
  }

  async function restore(id: number) {
    try {
      const response = await fetch(`/api/admin/bank-statements/${id}/restore`, {
        method: "POST",
        headers: h,
      });
      if (!response.ok) {
        toast({ title: "Could not restore statement", variant: "destructive" });
        return;
      }
      void loadBatches();
    } catch {
      toast({ title: "Could not restore statement", variant: "destructive" });
    }
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">
            Bank reconciliation
          </h1>
          <p className="text-sm text-slate-500">
            Upload your monthly RBS statement and reconcile it against the
            cashbook.
          </p>
        </div>

        {/* Upload */}
        <div className="rounded-xl border border-slate-200 p-4 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50 px-3 py-2 rounded-lg cursor-pointer">
              <Upload size={15} /> Choose CSV / PDF
              <input
                type="file"
                accept=".csv,.pdf"
                disabled={busy}
                onChange={onFile}
                className="hidden"
              />
            </label>
            <span className="text-xs text-slate-400">
              or paste the statement transactions below
            </span>
          </div>
          <textarea
            value={text}
            disabled={busy}
            onChange={(e) => {
              setText(e.target.value);
              setPreview(null);
            }}
            rows={5}
            placeholder="Paste RBS statement lines here (e.g. '20 Sep Jordan Hart Mobile/Digital Banking -£11.00')…"
            className="w-full border rounded-lg px-3 py-2 text-xs font-mono"
          />
          <button
            onClick={() => void doPreview()}
            disabled={busy}
            className="text-sm font-semibold text-white bg-primary px-4 py-2 rounded-lg disabled:opacity-50"
          >
            {busy ? "Reading…" : "Preview"}
          </button>
        </div>

        {/* Preview */}
        {preview && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 space-y-3">
            <div className="flex items-center gap-2 font-semibold text-slate-800">
              <FileText size={16} /> Preview — check this before saving
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <span>{preview.entries.length} transactions</span>
              <span>
                {preview.periodFrom} → {preview.periodTo}
              </span>
              <span className="text-emerald-700">
                In {pence(preview.totalInPence)}
              </span>
              <span className="text-rose-700">
                Out {pence(preview.totalOutPence)}
              </span>
            </div>
            {preview.warnings.length > 0 && (
              <div className="text-xs text-amber-800 flex items-start gap-1">
                <AlertTriangle size={13} className="mt-0.5" />{" "}
                {preview.warnings.join("; ")}
              </div>
            )}
            <div className="max-h-48 overflow-auto text-xs bg-white rounded border">
              <table className="w-full">
                <tbody>
                  {preview.entries.slice(0, 200).map((e, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td className="px-2 py-1 text-slate-500">
                        {e.entryDate}
                      </td>
                      <td className="px-2 py-1">{e.description}</td>
                      <td
                        className={`px-2 py-1 text-right ${e.direction === "in" ? "text-emerald-700" : "text-rose-700"}`}
                      >
                        {e.direction === "out" ? "-" : ""}
                        {pence(e.amountPence)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-xs text-slate-500">
              Compare the totals above against your statement's printed totals
              before saving.
            </div>
            <button
              onClick={() => void commit()}
              disabled={busy || preview.entries.length === 0}
              className="text-sm font-semibold text-white bg-emerald-600 px-4 py-2 rounded-lg disabled:opacity-50"
            >
              Looks right — save statement
            </button>
          </div>
        )}

        {/* Batches */}
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-500">
            Uploaded statements
          </div>
          <label className="block px-4 py-2 text-sm">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />{" "}
            Show archived statements
          </label>
          {batches.length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-sm">
              None yet
            </div>
          ) : (
            batches.map((b) => (
              <div
                key={b.id}
                className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-slate-100 text-sm"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">
                    {b.filename}{" "}
                    <span className="text-xs text-slate-400 uppercase">
                      {b.source}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500">
                    {b.periodFrom} → {b.periodTo} · {b.entryCount} txns · in{" "}
                    {pence(b.totalInPence)} / out {pence(b.totalOutPence)}
                  </div>
                </div>
                <div className="flex items-center justify-end flex-wrap gap-2 max-w-[45%]">
                  <button
                    disabled={b.archived}
                    onClick={() => void openRecon(b.id)}
                    className="text-xs font-semibold text-sky-600 border border-sky-200 hover:bg-sky-50 px-2.5 py-1 rounded"
                  >
                    Reconcile
                  </button>
                  {b.archived ? (
                    <button
                      onClick={() => void restore(b.id)}
                      className="text-xs underline"
                    >
                      Restore archived statement
                    </button>
                  ) : (
                    <button
                      aria-label="Archive statement"
                      onClick={() => void del(b.id)}
                      className="text-slate-300 hover:text-rose-600"
                    >
                      <Archive size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Reconciliation */}
        {recon && (
          <div className="rounded-xl border border-slate-200 p-4 space-y-3">
            <h3 className="font-semibold text-slate-800">
              Statement comparison — review required
            </h3>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div className="space-y-1">
                <div className="font-semibold text-slate-600">
                  Person payments and driver records
                </div>
                <div className="flex justify-between">
                  <span>Bank (to people)</span>
                  <span>{pence(recon.data.bank.personPaymentsOutPence)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Operator-reported driver transfers</span>
                  <span>{pence(recon.data.cashbook.driverPayoutsPence)}</span>
                </div>
                <div className="flex justify-between font-semibold text-amber-700">
                  <span className="flex items-center gap-1">
                    <AlertTriangle size={13} /> Unmatched amount difference
                  </span>
                  <span>{pence(recon.data.gaps.driverPayoutsPence)}</span>
                </div>
              </div>
              <div className="space-y-1">
                <div className="font-semibold text-slate-600">
                  Stripe bank deposits and fee projections
                </div>
                <div className="flex justify-between">
                  <span>Bank Stripe pay-ins</span>
                  <span>{pence(recon.data.bank.stripeInPence)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Recorded fee projection (not bank cash)</span>
                  <span>{pence(recon.data.cashbook.cardIncomePence)}</span>
                </div>
                <div className="flex justify-between text-slate-500">
                  <span>Difference requiring investigation</span>
                  <span>{pence(recon.data.gaps.stripeVsCardIncomePence)}</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-400">
              These totals do not match individual recipients or prove bank
              arrival. A small or zero difference is not confirmation. Review
              payment references and statement coverage before treating a driver
              or Stripe payout as received.
            </p>

            {stripePayouts.length > 0 && (
              <div className="pt-3 border-t border-slate-100">
                <div className="font-semibold text-slate-600 text-sm mb-1">
                  Stripe payouts to bank (match these to your "STRIPE PAYMENTS
                  UK" lines)
                </div>
                <div className="max-h-40 overflow-auto text-xs">
                  <table className="w-full">
                    <tbody>
                      {stripePayouts.map((p, i) => (
                        <tr key={i} className="border-b border-slate-50">
                          <td className="px-2 py-1 text-slate-500">
                            {p.arrivalDate}
                          </td>
                          <td className="px-2 py-1 text-slate-400">
                            {p.status}
                          </td>
                          <td className="px-2 py-1 text-right font-medium">
                            {pence(p.amountPence)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
